import { useEffect, useMemo, useState } from 'react';
import { SettingClient } from '../../../helpers/client';
import { useLocation } from 'react-router';
import { Box, IconButton } from '@mui/material';
import { useAppDispatch } from '../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { appStyleMode, basePath } from '../../../helpers/clientConfigs';
import * as externalLogin from '../../../helpers/externalLogin';
import { Spinner } from '../../spinner/spinner';
import { AppStyle } from '../../../helpers/appStyle';
import { authenticateFromApple } from '../../../redux/reducers/authReducer';
import { SignInProps } from '../../../interfaces/interfaces';
import { setRedirectAfterLogin } from '../../../helpers/localStorageService';
import AppleIcon from '@mui/icons-material/Apple';
import { ExternalAuthenticationRequest } from 'orderme-api-integration-client';
import { useRouteNavigator } from '../../../routes/useRouteNavigator';

export function AppleSignIn(props: SignInProps) {
  const dispatch = useAppDispatch();
  const routeNavigator = useRouteNavigator();
  const location = useLocation();
  useTranslation('account');
  const [clientId, setClientId] = useState('');
  const settingClient = useMemo(() => new SettingClient(), []);
  const [isBusy, setIsBusy] = useState(false);

  const handleAppleSignIn = async (
    accessToken?: string,
    state?: string,
    code?: string,
    firstName?: string,
    lastName?: string
  ) => {
    const appleState = externalLogin.getAppleState();
    externalLogin.removeAppleState();

    var appleToken = externalLogin.getAppleTokenProps(accessToken);

    if (!accessToken || !code || state !== appleState?.state || appleToken?.nonce !== appleState.nonce) {
      if (props.onError) {
        props.onError('Apple OAuth signIn failed');
      }
      return;
    }

    try {
      const authRequest = { accessToken: accessToken, state: state, code: code } as ExternalAuthenticationRequest;
      await dispatch(authenticateFromApple(authRequest)).unwrap();
      if (props.onSignedIn) {
        props.onSignedIn();
      }
    } catch (err) {
      console.log(err);
      const registrationProps = externalLogin.getAppleRegistrationProps(accessToken, state, code, firstName, lastName);
      routeNavigator.goToRegistration({ state: registrationProps, replace: true });
    }
  };

  useEffect(() => {
    if (clientId !== '') {
      return;
    }

    (async () => {
      let setting = await settingClient.get();

      if (!setting?.authentication?.appleClientId) {
        return;
      }

      setClientId(setting.authentication.appleClientId);
    })();
  }, [clientId, settingClient]);

  const signIn = () => {
    setIsBusy(true);

    const state = externalLogin.randomState();
    const nonce = externalLogin.randomNonce();
    externalLogin.saveAppleState(state, nonce);

    let redirectUrl = basePath + '/signin-apple';
    let endpoint =
      `https://appleid.apple.com/auth/authorize?` +
      `&client_id=${clientId}` +
      `&scope=name email` +
      `&redirect_uri=${redirectUrl}` +
      `&state=${state}` +
      `&nonce=${nonce}` +
      `&response_type=code id_token` +
      `&response_mode=form_post` +
      `&usePopup=false`;

    if (appStyleMode === AppStyle.Mobile) {
      signInMobileApp(endpoint);
    } else {
      setRedirectAfterLogin(location.pathname + location.search);
      window.location.replace(endpoint);
    }
  };

  const signInMobileApp = (endpoint: string) => {
    const browser = window.cordova.InAppBrowser.open(endpoint, '_blank', 'location=no');
    let oauthCompleted = false;

    const onLoadStart = async (event: any) => {
      const url: string = event.url;
      const callbackUrl = 'lt.paulini.mobile://oauth_callback';
      if (url.startsWith(callbackUrl) || url.startsWith('http://' + callbackUrl)) {
        oauthCompleted = true;
        browser.removeEventListener('loadstart', onLoadStart);
        browser.close();
        const fragment = url.includes('?') ? url.split('?')[1] : '';
        const params = new URLSearchParams(fragment);
        try {
          await handleAppleSignIn(
            params.get('access_token') ?? undefined,
            params.get('state') ?? undefined,
            params.get('code') ?? undefined,
            params.get('firstName') ?? undefined,
            params.get('lastName') ?? undefined
          );
        } finally {
          setIsBusy(false);
        }
      }
    };
    browser.addEventListener('loadstart', onLoadStart);
    browser.addEventListener('exit', () => {
      if (!oauthCompleted) {
        setIsBusy(false);
      }
    });
  };

  return (
    props.isVisible && (
      <Box>
        <IconButton
          onClick={signIn}
          sx={{
            border: '1px solid black',
            borderRadius: '10px',
            px: '20px',
            py: '10px',
          }}
        >
          <AppleIcon htmlColor="black" />
        </IconButton>
        <Spinner isActive={isBusy} />
      </Box>
    )
  );
}
